import { cn, StatTile } from "@dl/web-core-js/ui";
import { Check, PauseCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductionOrderOperationDTO } from "../../../api/floor";
import { formatNumber } from "../../../api/manufacturing";
import type { ProductionOrderDTO, ProductionOrderStatus } from "../../../api/production";
import { percent } from "../../../components/ProgressBar";
import { lifecycleOf, productionStatusLabel } from "../badges";
import { daysToDue, when } from "./lotModel";

/** When the order reached each status, as far as the record says. */
function reachedAt(lot: ProductionOrderDTO, status: ProductionOrderStatus): string | null | undefined {
  switch (status) {
    case "RELEASED":
    case "IN_PROGRESS":
      return lot.released_at;
    case "FIRMED":
      return lot.firmed_at;
    case "PRODUCED":
      return lot.produced_at;
    case "SHIPPING":
      return lot.shipping_at;
    case "COMPLETED":
    case "CLOSED":
      return lot.completed_at;
    default:
      return null;
  }
}

/**
 * Above the tabs: the hold banner, where the order is in its life (with the
 * day it reached each step), and the figures that say how it is going. A
 * production order: Laval's steps and its trucks; an assembly order: the sets
 * arriving, assembly, the shelf.
 */
export function LotHeader({ lot, steps }: { lot: ProductionOrderDTO; steps: ProductionOrderOperationDTO[] }) {
  const { t, i18n } = useTranslation();
  const flow = lifecycleOf(lot.kind);
  const cancelled = lot.status === "CANCELLED";
  const current = flow.indexOf(lot.status);
  const scrap = steps.reduce((sum, step) => sum + Number(step.quantity_scrap ?? 0), 0);
  const days = daysToDue(lot);
  const floorFollowed = (lot.component_count ?? 0) > 0;

  return (
    <div className="flex flex-col gap-3">
      {lot.is_on_hold && (
        <div className="flex items-center gap-2 rounded-md bg-warn-soft px-3 py-2 text-[13px] text-warn">
          <PauseCircle className="size-4 shrink-0" />
          <span className="font-medium">{t("lot.on-hold", "On hold")}</span>
          {lot.hold_reason && <span className="text-ink">{lot.hold_reason}</span>}
        </div>
      )}

      <ol className={cn("flex flex-wrap items-start gap-y-2", cancelled && "opacity-50")} aria-label={t("lot.stepper", "Where the lot is")}>
        {flow.map((status, index) => {
          const done = !cancelled && current >= 0 && index < current;
          const here = !cancelled && index === current;
          const at = reachedAt(lot, status);
          return (
            <li key={status} className="flex min-w-0 flex-1 basis-28 items-start">
              <div className="flex min-w-0 flex-col items-center gap-1 px-1 text-center">
                <span
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full border-2 text-[11px] font-semibold",
                    done && "border-ok bg-ok text-ink-on-accent",
                    here && "border-accent bg-accent-soft text-accent-strong",
                    !done && !here && "border-line text-ink-faint",
                  )}
                >
                  {done ? <Check className="size-3.5" strokeWidth={3} /> : index + 1}
                </span>
                <span className={cn("text-[12px] leading-tight", here ? "font-semibold text-ink" : done ? "text-ink" : "text-ink-faint")}>
                  {productionStatusLabel(t, status)}
                </span>
                {(done || here) && at && <span className="text-[11px] text-ink-faint">{when(at, i18n.language)}</span>}
              </div>
              {index < flow.length - 1 && (
                <span className={cn("mt-3 h-0.5 min-w-3 flex-1", done ? "bg-ok" : "bg-line")} aria-hidden />
              )}
            </li>
          );
        })}
      </ol>
      {cancelled && <p className="text-[13px] text-risk">{t("lot.cancelled", "This lot was cancelled.")}</p>}

      {floorFollowed && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatTile label={t("lot.kpi.progress", "Progress")} value={percent(lot.progress)} />
          <StatTile
            label={t("lot.kpi.components", "Components done")}
            value={`${String(lot.component_done_count ?? 0)}/${String(lot.component_count ?? 0)}`}
          />
          <StatTile label={t("lot.kpi.scrap", "Parts scrapped")} value={formatNumber(scrap)} />
          <StatTile
            label={t("lot.kpi.missing", "With parts missing")}
            value={<span className={(lot.missing_count ?? 0) > 0 ? "text-risk" : undefined}>{String(lot.missing_count ?? 0)}</span>}
          />
          <StatTile
            label={t("lot.kpi.due", "Days to due")}
            value={
              days == null ? (
                <span className="text-ink-faint">{t("lot.kpi.no-due", "none")}</span>
              ) : (
                <span className={days < 0 && lot.status !== "COMPLETED" ? "text-risk" : undefined}>{days}</span>
              )
            }
            note={lot.due_date ?? undefined}
          />
        </div>
      )}
    </div>
  );
}
